import React, { useCallback, useMemo } from 'react';
import Avatar from '@mui/material/Avatar';
import { BeautifulMentionsPlugin } from 'lexical-beautiful-mentions';
import { resolveUsernameColor } from '../../../utils/richText';
import { resolveAvatarUrl } from '../../../services/api';
import AvatarFallback from '../../AvatarFallback';

const MENTION_MAX = 8;

/**
 * @-mention autocomplete backed by lexical-beautiful-mentions.
 *
 * We feed it the room's online users via `onSearch` (prefix, case-insensitive,
 * excluding self, capped). Each suggestion carries the stable `id` in its data
 * so the inserted pill (nodes/BeautifulMention.js) keeps it, and the serializer
 * (serialize.js) emits the wire markup `@[value](id)` — unchanged contract with
 * richText.js / containsMentionOf().
 *
 * Custom menu + item components reuse ChatPanel's existing popover classes so
 * the visual matches what was there before.
 */
export default function MentionPlugin({ onlineUsers, currentUserId, classes, cx }) {
  // The library calls this on every keystroke after the trigger. Return the
  // suggestion list; `value` is the inserted/displayed text, everything else in
  // the object is stashed as the mention node's `data` (read back in serialize).
  const onSearch = useCallback(
    (_trigger, queryString) => {
      const q = (queryString || '').toLowerCase();
      const items = (onlineUsers || [])
        .filter((u) => u && u.username && u.id !== currentUserId)
        .filter((u) => u.username.toLowerCase().startsWith(q))
        .slice(0, MENTION_MAX)
        .map((u) => ({
          value: u.username,
          id: u.id,
          // resolveAvatarUrl maps "no avatar" / the server's placeholder to
          // undefined, so the row falls back to the builtin default avatar.
          avatar: resolveAvatarUrl(u.avatar),
          // Resolve to a concrete color (chosen or role default) so the inserted
          // pill renders in the user's own color — matching the sent message.
          usernameColor: resolveUsernameColor(u.usernameColor, u.role),
        }));
      return Promise.resolve(items);
    },
    [onlineUsers, currentUserId]
  );

  // Menu + item components close over `classes`/`cx`, so they must be built
  // here — but memoized on those (stable) deps so the library sees a STABLE
  // component identity and doesn't remount the popover on every keystroke.
  const Menu = useMemo(
    () =>
      // `loading` is a library-only prop (onSearch in-flight) — pull it out so
      // it isn't spread onto the <ul> as an invalid DOM attribute.
      React.forwardRef(function Menu({ children, loading, ...props }, ref) {
        return (
          <ul
            ref={ref}
            className={classes.mentionPopover}
            // LexicalTypeaheadMenuPlugin positions its anchor container 3px
            // BELOW the caret line (top = caretTop + lineHeight + 3) for
            // downward flow. Our composer sits at the bottom of the viewport,
            // so we flip the menu UP. `bottom: 100%` lifts it by its own
            // height; the extra marginBottom clears the caret line (~19px) +
            // the library's 3px gap so the menu's bottom lands just above the
            // current line's top.
            style={{
              position: 'absolute',
              bottom: '100%',
              left: 0,
              marginBottom: 26,
              listStyle: 'none',
            }}
            {...props}
          >
            {children}
          </ul>
        );
      }),
    [classes]
  );

  // One suggestion row. `item.data` holds the avatar / color we attached above.
  const MenuItem = useMemo(
    () =>
      React.forwardRef(function MenuItem(
        { selected, item, itemValue, label, ...props },
        ref
      ) {
        const data = (item && item.data) || {};
        const name = (item && item.value) || itemValue;
        return (
          <li
            ref={ref}
            className={cx(classes.mentionRow, selected && classes.mentionRowActive)}
            {...props}
          >
            {/* MUI Avatar falls back to its children whenever `src` is empty
                or fails to load — same idiom as the message list. */}
            <Avatar
              src={data.avatar || undefined}
              alt={name || ''}
              className={cx(classes.mentionAvatar, classes.avatarPhoto)}
            >
              <AvatarFallback letter={name?.charAt(0).toUpperCase() || '?'} />
            </Avatar>
            <span
              className={classes.mentionName}
              style={{ color: data.usernameColor || undefined }}
            >
              {name}
            </span>
          </li>
        );
      }),
    [classes, cx]
  );

  return (
    <BeautifulMentionsPlugin
      triggers={['@']}
      onSearch={onSearch}
      searchDelay={0}
      allowSpaces={false}
      menuComponent={Menu}
      menuItemComponent={MenuItem}
    />
  );
}
